import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { HumbeeTabs, CustomButton } from "../../../v2/MUI_custom_component";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import SvgOnboardBuyerOutlined from "../../../components/icons/OnboardBuyerOutlined";
import GlassModal from "../../../v2/MUI_custom_component/Modal/GlassModal";
import { AddEmployeeModal } from "./AddEmployeeModal";
import { MemberCard } from "./MemberCard";
import { addMemberAction } from "./API/Action";
import BentoBoxGrid from "../../../v2/MUI_custom_component/Bento/BentoBoxGrid";
import BentoItem from "../../../v2/MUI_custom_component/Bento/BentoItem";
import { PermissionModal } from "./Components/PermissionModal";
import {
  getEmployeeDetailsAction,
  getRolesAndPermissionDetailsAction,
} from "./API/Action";
import { EMPLOYEE_TYPES_ENUM } from "./Enum";
import { WhatsAppSection } from "./Components/WhatsAppSection";
import IllustrationWrapper from "../../Illustrration/IllustrationWrapper";
import successLottie from "../../../utils/Lottie/NewSuccess.json";

export const EmployeeDetails = () => {
  const isMobile = useMediaQuery("(max-width: 1200px)");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [allowedRoles, setAllowedRoles] = useState(null);
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.EmployeeReducer?.firm || []);
  const currentTab = useSelector(
    (state) => state.EmployeeReducer?.currentTab || 0,
  );
  const selectedCompany = useSelector(
    (state) => state.EmployeeReducer?.selectedFirm || null,
  );
  const showHistory = useSelector((state) => state.entityReducer.show_history);
  const [open, setOpen] = useState(false);
  const [showStaffPermissionModal, setShowStaffPermissionModal] =
    useState(false);
  const [pendingStaffPayload, setPendingStaffPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ownerWhatsappVerification, setOwnerWhatsappVerification] = useState({
    open: false,
    ownerId: null,
    firmId: null,
  });

  const getEmployeeDetails = async () => {
    setLoading(true);
    const { error, message, data } = await getEmployeeDetailsAction();
    setLoading(false);
    if (!error) {
      setEmployeeDetails(data);
    } else {
      toast.error(message);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      getAllowedRoles(selectedCompany.firm_id);
    }
  }, [selectedCompany, employeeDetails]);

  const getAllowedRoles = async (firmID) => {
    const { error, message, data } =
      await getRolesAndPermissionDetailsAction(firmID);
    if (!error) {
      setAllowedRoles(data.map(({ role }) => role));
      const { permissions } = data.find(
        ({ role }) => role.value === EMPLOYEE_TYPES_ENUM.STAFF,
      );
      dispatch({
        type: "PERMISSION_LIST",
        data: permissions,
      });
    } else {
      toast.error(message);
    }
  };
  useEffect(() => {
    getEmployeeDetails();
  }, []);

  useEffect(() => {
    if (employeeDetails && employeeDetails.length > 0) {
      const firms = employeeDetails.map((firm) => ({
        firm_id: firm.firm_id,
        firm_name: firm.firm_name,
      }));
      dispatch({ type: "SET_FIRMS", data: firms });
      if (firms.length > 0 && !selectedCompany) {
        const initialFirm = firms[0];
        dispatch({ type: "SELECTED_FIRM", data: initialFirm });
      }
    }
  }, [employeeDetails, dispatch, selectedCompany]);

  const tabData = companies.length > 0 ? companies : employeeDetails;

  const handleTabChange = (event, newValue) => {
    const tabData = companies.length > 0 ? companies : employeeDetails;
    if (tabData && tabData[newValue]) {
      const currentSelectedFirm = {
        firm_id: tabData[newValue].firm_id,
        firm_name: tabData[newValue].firm_name,
      };
      dispatch({ type: "SELECTED_FIRM", data: currentSelectedFirm });
    }
  };

  const handleAddMember = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setShowStaffPermissionModal(false);
    setPendingStaffPayload(null);
    setOpen(false);
  };

  const handleStaffOtpVerified = (payload) => {
    setPendingStaffPayload(payload);
    setShowStaffPermissionModal(true);
  };

  const handleStaffPermissionSave = async ({ permissions }) => {
    if (!pendingStaffPayload) {
      return;
    }

    const payload = {
      ...pendingStaffPayload,
      staff_details: {
        ...pendingStaffPayload.staff_details,
        permissions: permissions.flat(),
      },
    };
    const { error, message } = await addMemberAction(payload);
    if (error) {
      toast.error(message || "Failed to add member");
      handleClose();
      return;
    }

    toast.success(message || "Member added successfully");
    await getEmployeeDetails();
    handleClose();
  };

  const handleOwnerAddedRequiresWhatsapp = ({ ownerId, firmId }) => {
    setOwnerWhatsappVerification({ open: true, ownerId, firmId });
  };

  const handleCloseOwnerWhatsappVerification = () => {
    setOwnerWhatsappVerification({ open: false, ownerId: null, firmId: null });
    getEmployeeDetails();
  };

  const selectedFirmEmployees =
    employeeDetails?.find((firm) => firm.firm_id === selectedCompany?.firm_id)
      ?.staff_list || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 0.5, md: 1 },
        }}>
        <Typography variant="h1">Staff</Typography>
        <HumbeeTabs
          tabVariant={isMobile ? "small" : "default"}
          data={tabData}
          currentTab={currentTab}
          onChange={handleTabChange}
          getTabLabel={(firm) => firm.firm_name}
          getTabKey={(firm) => firm.firm_id}
          tabType="dashed"
          variant="scrollable"
          scrollButtons="auto"
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: { xs: "100%", md: "50%" },
            minWidth: 0,
            gap: 2,
          }}>
          <Typography>Would you like to add members?</Typography>
          <CustomButton
            variant="filled"
            color="primary"
            label="Add"
            disabled={showHistory}
            size={isMobile ? "small" : "medium"}
            leftIcon={SvgOnboardBuyerOutlined}
            onClick={handleAddMember}
          />
        </Box>

        <BentoBoxGrid column={isMobile ? 1 : 2} gap={2}>
          {selectedFirmEmployees.map((m) => {
            return (
              <BentoItem
                key={m.id}
                colSpan={isMobile ? 1 : 1}
              >
              <MemberCard
                key={m.id}
                member={m}
                fetchEmployeeDetails={getEmployeeDetails}
              />
              </BentoItem>
              );
            })
          }
        </BentoBoxGrid>
      </Box>
      <GlassModal
        open={open}
        onClose={handleClose}
        title="Add Member"
        scrollable={true}
        maxWidth="600px"
        zIndex={2}>
        {!showStaffPermissionModal ? (
          <AddEmployeeModal
            firmID={selectedCompany?.firm_id}
            onClose={handleClose}
            allowedRoles={allowedRoles}
            fetchEmployeeDetails={getEmployeeDetails}
            setShowStaffPermissionModal={setShowStaffPermissionModal}
            onStaffOtpVerified={handleStaffOtpVerified}
            onOwnerAddedRequiresWhatsapp={handleOwnerAddedRequiresWhatsapp}
          />
        ) : (
          <PermissionModal
            initialPermissions={[]}
            onSave={handleStaffPermissionSave}
            onClose={() => {
              setShowStaffPermissionModal(false);
              handleClose();
            }}
          />
        )}
      </GlassModal>
      <GlassModal
        open={ownerWhatsappVerification.open}
        onClose={handleCloseOwnerWhatsappVerification}
        title="Verify WhatsApp Number"
        scrollable={true}
        maxWidth="600px"
        showHeader={false}
        keepMounted={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2 }, width: '100%' }}>
          <Box sx={{ textAlign: "center", justifyContent: "center", display: "flex", flexDirection: "column", gap: 1 }}>
            <IllustrationWrapper
              titleVariant="h1"
              subtitleVariant="body1"
              animationData={successLottie}
              loop={false}
              onClose={handleCloseOwnerWhatsappVerification}
              title="Successful!"
              subtitle={'Owner added successfully.'}
            />
          </Box>
          <WhatsAppSection
            is_whatsapp_same={false}
            OwnerWhatsappNumber={null}
            ownerKey={ownerWhatsappVerification.ownerId}
            labelDirection="column"
            firmId={ownerWhatsappVerification.firmId || selectedCompany?.firm_id}
            isEditable={true}
            onWhatsappVerified={handleCloseOwnerWhatsappVerification}
            verifyWhatsappProps={{
              showMobileNumber: true,
              showText: true,
              containerSx: { width: '100%', alignItems: 'center', gap: 1 },
              textFieldSx: {
                width: '100%',
                '& legend': { textAlign: 'left' },
                '& .MuiTypography-root': { textAlign: 'left' },
              },
              showActionButtons: true,
              cancelLabel: 'Cancel',
              verifyLabel: 'Verify',
              onCancel: handleCloseOwnerWhatsappVerification,
              onOpenVerifyModal: () =>
                setOwnerWhatsappVerification({
                  open: false,
                  ownerId: ownerWhatsappVerification.ownerId,
                  firmId: ownerWhatsappVerification.firmId,
                }),
            }}
          />
        </Box>
      </GlassModal>
    </Box>
  );
};


